import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Explicit UI state for the Agency Workspace: which salon (tenant) the
 * receptionist/admin is currently operating on. NEVER derived from the URL
 * pathname. Persisted in localStorage but ALWAYS re-validated against the
 * server-authorized list returned by GET /api/agency/tenants.
 */
const Ctx = createContext(null);
const LS_KEY = "mem_agency_tenant";

export function AgencyWorkspaceProvider({ children }) {
  const [availableTenants, setAvailable] = useState([]);
  const [selectedTenantId, setSelectedId] = useState(() => localStorage.getItem(LS_KEY) || null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/agency/tenants");
      setAvailable(r.data || []);
    } catch {
      setAvailable([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const persist = (id) => {
    if (id) localStorage.setItem(LS_KEY, id);
    else localStorage.removeItem(LS_KEY);
  };

  // Re-validate the persisted/selected tenant against the authorized list.
  useEffect(() => {
    if (loading) return;
    const ids = availableTenants.map((t) => t.id);
    if (selectedTenantId && !ids.includes(selectedTenantId)) {
      const next = ids[0] || null;
      setSelectedId(next); persist(next);
    } else if (!selectedTenantId && ids.length) {
      setSelectedId(ids[0]); persist(ids[0]);
    }
  }, [loading, availableTenants]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSelectedTenant = useCallback((id) => {
    setSelectedId(id); persist(id);
  }, []);

  const selectedTenant = availableTenants.find((t) => t.id === selectedTenantId) || null;

  return (
    <Ctx.Provider value={{
      availableTenants, selectedTenantId, selectedTenant,
      setSelectedTenant, loading, reload: load,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAgencyWorkspace = () => useContext(Ctx);
